import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Pressable, View } from 'react-native'
import { Popover as RNPopper } from 'react-native-popper'

import styled, { useTheme } from 'styled-components/native'

import { getTimeZoneName } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { CheckIcon } from '../../atoms/icons/CheckIcon'
import { GlobeIcon } from '../../atoms/icons/GlobeIcon'
import { RefreshIcon } from '../../atoms/icons/RefreshIcon'
import { IS_WEB } from '../../constants'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface CalendarTimeZoneSelectorProps {
  /** Currently selected timezone (IANA format, e.g., 'America/Los_Angeles') */
  selectedTimeZone: string
  /** Provider's configured timezone (IANA format) - used for reset option */
  providerTimeZone: string
  /** Callback when a timezone is selected */
  onTimeZoneSelected: (timezone: string) => void
  /** Test ID prefix */
  testID?: string
}

// US timezones in the order specified by design
const US_TIMEZONES = [
  { id: 'America/Puerto_Rico', city: 'Puerto Rico' },
  { id: 'America/New_York', city: 'New York' },
  { id: 'America/Chicago', city: 'Chicago' },
  { id: 'America/Denver', city: 'Denver' },
  { id: 'America/Los_Angeles', city: 'Los Angeles' },
  { id: 'America/Anchorage', city: 'Anchorage' },
  { id: 'Pacific/Honolulu', city: 'Honolulu' },
] as const

// Pill-style trigger button matching Figma design
const TriggerButton = styled(Pressable)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing['4px'],
  paddingHorizontal: theme.spacing['12px'],
  paddingVertical: theme.spacing['8px'],
  borderRadius: 100, // Fully rounded pill
  backgroundColor: theme.colors.backgroundHighlightPrimary,
}))

// Dropdown container with rounded corners and border
const DropdownContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  paddingVertical: theme.spacing['8px'],
  borderRadius: theme.spacing['16px'],
  borderWidth: 1,
  borderColor: theme.colors.borderDefault,
  boxShadow: `0 4px 8px ${theme.colors.shadowLow}`,
  width: 407,
  maxHeight: 400,
}))

// Header item (reset option) with bottom border
const HeaderItemContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  borderBottomWidth: 1,
  borderBottomColor: theme.colors.borderDefault,
  paddingHorizontal: theme.spacing['16px'],
  paddingVertical: theme.spacing['8px'],
}))

const HeaderItemPressable = styled(Pressable)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

// Regular menu item with padding for hover/selected state
const MenuItemContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingHorizontal: theme.spacing['8px'],
}))

const MenuItemPressable = styled(Pressable)<{ theme: ThemeType; isSelected?: boolean }>(({ theme, isSelected }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: theme.spacing['16px'],
  paddingVertical: theme.spacing['8px'],
  borderRadius: theme.spacing['8px'],
  gap: theme.spacing['16px'],
  ...(isSelected && {
    backgroundColor: theme.colors.backgroundSection,
  }),
}))

const MenuItemText = styled(BodyText)({
  flex: 1,
})

export const CalendarTimeZoneSelector: FunctionComponent<CalendarTimeZoneSelectorProps> = ({
  selectedTimeZone,
  providerTimeZone,
  onTimeZoneSelected,
  testID = 'CalendarTimeZoneSelector',
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme() as ThemeType
  const triggerRef = useRef<View>(null)
  const [isOpen, setIsOpen] = useState(false)

  const selectedAbbreviation = useMemo(
    () => getTimeZoneName({ timeZone: selectedTimeZone, timeZoneName: 'short' }) ?? selectedTimeZone,
    [selectedTimeZone],
  )

  const providerAbbreviation = useMemo(
    () => getTimeZoneName({ timeZone: providerTimeZone, timeZoneName: 'short' }) ?? providerTimeZone,
    [providerTimeZone],
  )

  const handleSelect = useCallback(
    (timezoneId: string) => {
      onTimeZoneSelected(timezoneId)
      setIsOpen(false)
    },
    [onTimeZoneSelected],
  )

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
  }, [])

  return (
    <>
      <View ref={triggerRef}>
        <TriggerButton
          testID={tID(testID)}
          onPress={() => setIsOpen((prev) => !prev)}
          accessibilityRole='button'
          accessibilityLabel={formatMessage({
            defaultMessage: 'Select timezone',
            description: 'Accessibility label for timezone selector button',
          })}
        >
          <GlobeIcon size={12} fillColor={theme.colors.textActive} />
          <BodyText text={selectedAbbreviation} color={theme.colors.textActive} size={BodyTextSize.BADGE} />
        </TriggerButton>
      </View>
      <RNPopper
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        trigger={triggerRef}
        placement='bottom left'
        offset={4}
        shouldCloseOnOutsideClick
      >
        {!IS_WEB && <RNPopper.Backdrop />}
        <RNPopper.Content>
          <DropdownContainer testID={tID(`${testID}-dropdown`)}>
            {/* Reset to provider timezone option */}
            <HeaderItemContainer>
              <HeaderItemPressable
                testID={tID(`${testID}-reset-option`)}
                onPress={() => handleSelect(providerTimeZone)}
                accessibilityRole='button'
                accessibilityLabel={formatMessage(
                  {
                    defaultMessage: 'Reset to provider timezone ({abbreviation})',
                    description: 'Accessibility label for reset timezone button',
                  },
                  { abbreviation: providerAbbreviation },
                )}
              >
                <RefreshIcon size={24} fillColor={theme.colors.linkDefault} />
                <MenuItemText
                  text={formatMessage(
                    {
                      defaultMessage: 'Reset to provider timezone ({abbreviation})',
                      description: 'Option to reset timezone to provider configured timezone',
                    },
                    { abbreviation: providerAbbreviation },
                  )}
                  size={BodyTextSize.DEFAULT}
                  color={theme.colors.linkDefault}
                />
              </HeaderItemPressable>
            </HeaderItemContainer>

            {/* US timezone options */}
            {US_TIMEZONES.map((tz) => {
              const longName = getTimeZoneName({ timeZone: tz.id, timeZoneName: 'long' }) ?? tz.id
              const isSelected = selectedTimeZone === tz.id
              const displayText = formatMessage(
                {
                  defaultMessage: '{longName} - {city}',
                  description: 'Timezone option display text with long name and city',
                },
                { longName, city: tz.city },
              )

              return (
                <MenuItemContainer key={tz.id}>
                  <MenuItemPressable
                    testID={tID(`${testID}-option-${tz.id}`)}
                    onPress={() => handleSelect(tz.id)}
                    isSelected={isSelected}
                    accessibilityRole='button'
                    accessibilityLabel={displayText}
                    accessibilityState={{ selected: isSelected }}
                  >
                    <MenuItemText
                      text={displayText}
                      size={BodyTextSize.DEFAULT}
                      color={isSelected ? theme.colors.textActive : theme.colors.textSecondary}
                    />
                    {isSelected && <CheckIcon size={24} fillColor={theme.colors.textActive} withCircle={false} />}
                  </MenuItemPressable>
                </MenuItemContainer>
              )
            })}
          </DropdownContainer>
        </RNPopper.Content>
      </RNPopper>
    </>
  )
}
